import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ServerIcon,
  SearchIcon,
  FilterIcon,
  PlayIcon,
  SquareIcon,
  RotateCcwIcon,
  Trash2Icon,
  RefreshCwIcon,
  AlertTriangleIcon,
} from 'lucide-react'
import { useQueryState, debounce } from 'nuqs'
import { useState, useRef, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'

import { ResponsiveActionRow, ResponsivePageHeader } from '@nebula/shared/components'
import { ConfirmModal, AlertModal, ConfirmDestroyModal } from '@nebula/shared/components/modals'
import { usePermissions } from '@nebula/shared/hooks'
import { apiClient } from '@nebula/shared/services/api'
import { useAuthStore } from '@nebula/shared/store'
import {
  AVISO_PROVISIONAMENTO_INCOMPLETO,
  getErrorMessage,
  provisionamentoIncompleto,
} from '@nebula/shared/utils'

import InfraSetupBanner from '../components/InfraSetupBanner'
import { useInfraStatus } from '../hooks/useInfraStatus'
import {
  montarAlertaDeSincronizacao,
  type RespostaDeSincronizacaoDeStatus,
} from './sincronizacaoDeStatusAlerta'

interface Instance {
  instance_id: string
  instance_name: string
  product: {
    id: string
    name: string
    description: string
    product_type: string
  }
  reseller: {
    id: string
    name: string
  }
  customer: {
    id: string
    name: string
  }
  quantity: number
  pricing: {
    final_price: number
    currency: string
    billing_cycle: string
  }
  status: string
  billing_status: string
  /** VM que existe no servidor com a criação pela metade (#803). */
  provisioning_incomplete?: boolean
  created_at: string
  estimated_ready_time?: string
  cpu_cores?: number
  memory_gb?: number
  disk_gb?: number
  ip_addresses?: string[]
  os_display_name?: string | null
}

interface InstancesResponse {
  instances: Instance[]
}

interface VmActionResponse {
  message: string
  billing_change: string
}

interface DeleteInstanceResponse {
  message: string
  data?: unknown
}

const InstancesPage = () => {
  const infraStatus = useInfraStatus()
  const [searchTerm, setSearchTerm] = useQueryState('q', {
    defaultValue: '',
    history: 'replace',
    clearOnDefault: true,
    limitUrlUpdates: debounce(300),
  })
  const [statusFilter, setStatusFilter] = useQueryState('status', {
    defaultValue: '',
    history: 'replace',
    clearOnDefault: true,
  })
  const [showFilters, setShowFilters] = useState(false)
  const trimmedSearch = searchTerm.trim()
  // Modal states
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean
    title: string
    message: string
    onConfirm: () => void
    variant?: 'danger' | 'warning' | 'info'
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  })

  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean
    title: string
    message: string
    variant?: 'success' | 'error' | 'warning' | 'info'
  }>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info',
  })

  const [destroyModal, setDestroyModal] = useState<{
    isOpen: boolean
    instanceId: string
    instanceName: string
  }>({
    isOpen: false,
    instanceId: '',
    instanceName: '',
  })

  const { token } = useAuthStore()
  const { has } = usePermissions()
  const canStartVm = has('vms:start')
  const canStopVm = has('vms:stop')
  const canRestartVm = has('vms:restart')
  const canDeleteVm = has('vms:delete')

  // Track previous statuses for transition detection
  const prevStatusMap = useRef<Record<string, string>>({})

  // Track mount time for adaptive polling (2s first 20s, then 30s)
  const mountTime = useRef(Date.now())

  // Get instances from API
  const {
    data: instancesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['end-customer-instances', statusFilter],
    enabled: !!token,
    queryFn: async () => {
      const params = new URLSearchParams()
      if (statusFilter) params.append('status_filter', statusFilter)

      const response = await apiClient.get<InstancesResponse>(
        `/api/v1/end-customer/instances?${params.toString()}`,
      )

      return response
    },
    refetchInterval: () => {
      const elapsed = Date.now() - mountTime.current
      return elapsed < 20_000 ? 2_000 : 30_000
    },
    // structural sharing: React Query only triggers re-renders if data actually changed
    structuralSharing: true,
  })

  // Sort by creation date — newest first
  const instances = useMemo(() => {
    const list = instancesData?.instances || []
    const filtered = trimmedSearch
      ? list.filter((i: Instance) =>
          i.instance_name.toLowerCase().includes(trimmedSearch.toLowerCase()),
        )
      : list
    return [...filtered].sort(
      (a: Instance, b: Instance) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )
  }, [instancesData, trimmedSearch])
  const queryClient = useQueryClient()

  // Detect status transitions and show toasts
  useEffect(() => {
    if (!instances.length) return

    const prev = prevStatusMap.current
    for (const instance of instances) {
      const oldStatus = prev[instance.instance_id]
      if (oldStatus === 'creating' && instance.status === 'running') {
        toast.success(`VM "${instance.instance_name}" provisionada com sucesso!`)
      } else if (oldStatus === 'creating' && instance.status === 'error') {
        toast.error(`Falha ao provisionar VM "${instance.instance_name}"`)
      }
    }

    // Update ref with current statuses
    const newMap: Record<string, string> = {}
    for (const instance of instances) {
      newMap[instance.instance_id] = instance.status
    }
    prevStatusMap.current = newMap
  }, [instances])

  // VM control mutation
  const controlVmMutation = useMutation({
    mutationFn: async ({ instanceId, action }: { instanceId: string; action: string }) => {
      const response = await apiClient.post<VmActionResponse>(
        `/api/v1/end-customer/instances/${instanceId}/action`,
        {
          action,
        },
      )
      return response
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['end-customer-instances'] })
      setAlertModal({
        isOpen: true,
        title: 'Ação Concluída',
        message: `${data.message} ${data.billing_change}`,
        variant: 'success',
      })
    },
    onError: (error) => {
      console.error('❌ Error controlling VM:', error)
      setAlertModal({
        isOpen: true,
        title: 'Erro',
        message: 'Erro ao controlar VM. Tente novamente.',
        variant: 'error',
      })
    },
  })

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    // Query will be automatically triggered by the dependency
  }

  // VM delete mutation
  const deleteInstanceMutation = useMutation({
    mutationFn: async ({
      instanceId,
      confirmDeleteBackups = false,
    }: {
      instanceId: string
      confirmDeleteBackups?: boolean
    }) => {
      const params = confirmDeleteBackups ? '?confirm_delete_backups=true' : ''
      const response = await apiClient.delete<DeleteInstanceResponse>(
        `/api/v1/end-customer/instances/${instanceId}${params}`,
      )
      return response
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['end-customer-instances'] })
      setDestroyModal({ isOpen: false, instanceId: '', instanceName: '' })
      setAlertModal({
        isOpen: true,
        title: 'VM Deletada',
        message: data.message || 'VM removida da lista com sucesso!',
        variant: 'success',
      })
    },
    onError: (error: any, variables) => {
      const detail = error?.response?.data?.detail || error?.response?.data?.error

      // VM has backup schedules — ask for confirmation
      if (error?.response?.status === 409 && detail?.code === 'has_backup_schedules') {
        const scheduleNames = detail.schedules?.map((s: any) => s.name).join(', ') || ''
        setDestroyModal({ isOpen: false, instanceId: '', instanceName: '' })
        setConfirmModal({
          isOpen: true,
          title: 'VM possui agendamentos de backup',
          message: `${detail.message}\n\nAgendamentos: ${scheduleNames}\n\nDeseja continuar e remover tudo?`,
          variant: 'danger',
          onConfirm: () =>
            deleteInstanceMutation.mutate({
              instanceId: variables.instanceId,
              confirmDeleteBackups: true,
            }),
        })
        return
      }

      setDestroyModal({ isOpen: false, instanceId: '', instanceName: '' })
      const errorMessage =
        typeof detail === 'string'
          ? detail
          : detail?.message || error?.message || 'Erro desconhecido'
      setAlertModal({
        isOpen: true,
        title: 'Erro ao Deletar',
        message: errorMessage,
        variant: 'error',
      })
    },
  })

  // Handle VM control actions
  const handleVmAction = (instanceId: string, action: string) => {
    const actionMap = {
      start: 'iniciar',
      stop: 'parar',
      restart: 'reiniciar',
    }

    setConfirmModal({
      isOpen: true,
      title: `Confirmar ${actionMap[action as keyof typeof actionMap]}`,
      message: `Tem certeza que deseja ${actionMap[action as keyof typeof actionMap]} esta VM?`,
      variant: action === 'stop' ? 'warning' : 'info',
      onConfirm: () => controlVmMutation.mutate({ instanceId, action }),
    })
  }

  // Handle VM delete
  const handleVmDelete = (instanceId: string, instanceName: string) => {
    setDestroyModal({ isOpen: true, instanceId, instanceName })
  }

  const closeDestroyModal = () => {
    setDestroyModal({ isOpen: false, instanceId: '', instanceName: '' })
  }

  // Sync realtime status mutation
  const syncStatusMutation = useMutation({
    mutationFn: () =>
      apiClient.get<RespostaDeSincronizacaoDeStatus>(
        '/api/v1/end-customer/instances/realtime-status',
      ),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['end-customer-instances'] })
      // Três desfechos por VM (#767) — inclusive a VM que o Proxmox não tem mais e a
      // que não pôde ser verificada; nenhum deles é "já estava correto".
      setAlertModal({ isOpen: true, ...montarAlertaDeSincronizacao(data) })
    },
    onError: (error: unknown) => {
      console.error('❌ Error syncing status:', error)
      // O handler global embrulha em `{error:{message}}`; `getErrorMessage` lê os dois formatos.
      setAlertModal({
        isOpen: true,
        title: 'Erro na Sincronização',
        message: getErrorMessage(error),
        variant: 'error',
      })
    },
  })

  // Format relative time
  const formatRelativeTime = (dateString: string) => {
    const now = Date.now()
    const date = new Date(dateString).getTime()
    const diff = now - date
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)
    if (minutes < 1) return 'agora'
    if (minutes < 60) return `${minutes} min atrás`
    if (hours < 24) return `${hours}h atrás`
    if (days < 30) return `${days}d atrás`
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    })
  }

  // Format specs line
  const formatSpecs = (instance: Instance) => {
    const parts: string[] = []
    if (instance.memory_gb)
      parts.push(
        instance.memory_gb >= 1
          ? `${instance.memory_gb} GB`
          : `${Math.round(instance.memory_gb * 1024)} MB`,
      )
    if (instance.disk_gb) parts.push(`${instance.disk_gb} GB Disk`)
    if (instance.cpu_cores) parts.push(`${instance.cpu_cores} vCPU`)
    return parts.join(' / ')
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
            <ServerIcon className="h-7 w-7 animate-pulse text-slate-400" />
          </div>
          <p className="text-sm text-slate-500">Carregando suas instâncias...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-red-50">
            <ServerIcon className="h-8 w-8 text-red-400" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-slate-900">Erro ao carregar VMs</h3>
          <p className="mb-6 text-sm text-slate-500">
            {error instanceof Error ? error.message : 'Erro desconhecido'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-slate-800"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="mobile-page-padding">
      <ResponsivePageHeader
        title="Instâncias"
        description="Gerencie suas instâncias e monitore a cobrança"
        icon={ServerIcon}
        actions={
          <ResponsiveActionRow justify="end" className="w-full sm:w-auto">
            <button
              onClick={() => syncStatusMutation.mutate()}
              disabled={syncStatusMutation.isPending}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-slate-800 disabled:opacity-50"
              title="Atualizar o status das instâncias com os dados mais recentes"
            >
              <RefreshCwIcon
                className={`h-4 w-4 ${syncStatusMutation.isPending ? 'animate-spin' : ''}`}
              />
              <span>Sincronizar Status</span>
            </button>
          </ResponsiveActionRow>
        }
      />

      {/* Search and Filters */}
      <div className="mb-6 rounded-2xl border border-slate-200/60 bg-white p-5 shadow-sm">
        <form onSubmit={handleSearch} className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <SearchIcon className="absolute top-1/2 left-3.5 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar instâncias por nome..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border border-slate-200 py-2.5 pr-4 pl-10 text-sm transition-colors placeholder:text-slate-400 focus:border-slate-300 focus:ring-2 focus:ring-slate-900/10"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 sm:w-auto"
          >
            <FilterIcon className="h-4 w-4" />
            <span>Filtros</span>
          </button>
        </form>

        {/* Expanded Filters */}
        {showFilters && (
          <div className="mt-4 grid grid-cols-1 gap-4 border-t border-slate-100 pt-4 md:grid-cols-2">
            <div>
              <label
                htmlFor="instance-status-filter"
                className="mb-2 block text-xs font-semibold tracking-wider text-slate-500 uppercase"
              >
                Status
              </label>
              <select
                id="instance-status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-slate-300 focus:ring-2 focus:ring-slate-900/10"
              >
                <option value="">Todos os status</option>
                <option value="creating">Criando</option>
                <option value="running">Executando</option>
                <option value="stopped">Parada</option>
                <option value="error">Erro</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Instances Table */}
      {instances && instances.length > 0 ? (
        <div className="overflow-hidden rounded-2xl border border-slate-200/60 bg-white shadow-sm">
          {/* Table header */}
          <div className="hidden gap-4 border-b border-slate-100 bg-slate-50/50 px-6 py-3 md:grid md:grid-cols-[1fr_160px_140px_120px_100px]">
            <span className="text-xs font-semibold tracking-wider text-slate-500 uppercase">
              Nome
            </span>
            <span className="text-xs font-semibold tracking-wider text-slate-500 uppercase">
              IP
            </span>
            <span className="text-xs font-semibold tracking-wider text-slate-500 uppercase">
              Criado
            </span>
            <span className="text-xs font-semibold tracking-wider text-slate-500 uppercase">
              Status
            </span>
            <span className="text-right text-xs font-semibold tracking-wider text-slate-500 uppercase">
              Ações
            </span>
          </div>

          {/* Table rows */}
          <div className="divide-y divide-slate-100">
            {instances.map((instance) => {
              const specs = formatSpecs(instance)
              const ip = instance.ip_addresses?.[0] || '—'
              const statusColors: Record<string, string> = {
                running: 'bg-emerald-500',
                stopped: 'bg-slate-300',
                creating: 'bg-amber-400 animate-pulse',
                error: 'bg-red-500',
              }
              const statusLabels: Record<string, string> = {
                running: 'Ativa',
                stopped: 'Parada',
                creating: 'Criando',
                error: 'Erro',
              }
              const statusDot = statusColors[instance.status] || 'bg-slate-300'

              return (
                <Link
                  key={instance.instance_id}
                  to={`/instances/${instance.instance_id}`}
                  className="grid cursor-pointer grid-cols-1 items-center gap-2 px-6 py-4 transition-colors hover:bg-slate-50/70 md:grid-cols-[1fr_160px_140px_120px_100px] md:gap-4"
                >
                  {/* Name + specs */}
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100">
                      <ServerIcon className="h-5 w-5 text-slate-500" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-medium text-slate-900">
                        {instance.instance_name}
                      </h3>
                      <p className="truncate text-xs text-slate-400">
                        {specs}
                        {instance.os_display_name ? ` — ${instance.os_display_name}` : ''}
                      </p>
                      {/* O status desta VM pode dizer "Ativa" e ainda assim faltar
                          credencial, rede ou o disco contratado (#803) — daí o aviso
                          vir junto do nome, e não da coluna de status. */}
                      {provisionamentoIncompleto(instance) && (
                        <p className="mt-0.5 flex items-center gap-1 text-xs font-medium text-amber-600">
                          <AlertTriangleIcon className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">
                            {AVISO_PROVISIONAMENTO_INCOMPLETO.titulo}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* IP */}
                  <div className="md:block">
                    <span className="font-mono text-sm text-slate-600">{ip}</span>
                  </div>

                  {/* Created */}
                  <div>
                    <span className="text-sm text-slate-500">
                      {formatRelativeTime(instance.created_at)}
                    </span>
                  </div>

                  {/* Status */}
                  <div className="flex items-center gap-2">
                    <div className={`h-2 w-2 rounded-full ${statusDot}`} />
                    <span className="text-xs text-slate-500 capitalize">
                      {statusLabels[instance.status] || 'Desconhecido'}
                    </span>
                  </div>

                  {/* Actions */}
                  <div
                    className="flex items-center justify-end gap-1"
                    onClick={(e) => e.preventDefault()}
                  >
                    {instance.status === 'running'
                      ? canStopVm && (
                          <button
                            onClick={() => handleVmAction(instance.instance_id, 'stop')}
                            disabled={controlVmMutation.isPending}
                            className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                            title="Parar"
                          >
                            <SquareIcon className="h-4 w-4" />
                          </button>
                        )
                      : canStartVm && (
                          <button
                            onClick={() => handleVmAction(instance.instance_id, 'start')}
                            disabled={controlVmMutation.isPending}
                            className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-emerald-50 hover:text-emerald-600"
                            title="Iniciar"
                          >
                            <PlayIcon className="h-4 w-4" />
                          </button>
                        )}
                    {canRestartVm && (
                      <button
                        onClick={() => handleVmAction(instance.instance_id, 'restart')}
                        disabled={controlVmMutation.isPending}
                        className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                        title="Reiniciar"
                      >
                        <RotateCcwIcon className="h-4 w-4" />
                      </button>
                    )}
                    {canDeleteVm && (
                      <button
                        onClick={() => handleVmDelete(instance.instance_id, instance.instance_name)}
                        disabled={deleteInstanceMutation.isPending || instance.status === 'running'}
                        className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-30"
                        title="Deletar"
                      >
                        <Trash2Icon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </Link>
              )
            })}
          </div>
        </div>
      ) : (
        <div className="py-16 text-center">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100">
            <ServerIcon className="h-8 w-8 text-slate-400" />
          </div>
          {trimmedSearch ? (
            <>
              <h3 className="mb-2 text-lg font-semibold text-slate-900">
                Nenhuma instância encontrada
              </h3>
              <p className="mx-auto mb-8 max-w-sm text-sm text-slate-500">
                Nenhuma instância corresponde a "{trimmedSearch}". Tente buscar por outro nome.
              </p>
              <button
                onClick={() => setSearchTerm('')}
                className="rounded-xl bg-slate-900 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-slate-800"
              >
                Limpar busca
              </button>
            </>
          ) : (
            <>
              <h3 className="mb-2 text-lg font-semibold text-slate-900">
                Nenhuma instância criada
              </h3>
              <p className="mx-auto mb-8 max-w-sm text-sm text-slate-500">
                Você ainda não possui nenhuma instância. Que tal criar sua primeira?
              </p>
              {!infraStatus.isReady ? (
                <InfraSetupBanner infraStatus={infraStatus} />
              ) : (
                <button
                  onClick={() => (window.location.href = '/marketplace')}
                  className="rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-500 px-6 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:from-emerald-700 hover:to-emerald-600"
                >
                  Explorar Marketplace
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Modais */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        isLoading={controlVmMutation.isPending || deleteInstanceMutation.isPending}
      />

      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
      />

      <ConfirmDestroyModal
        isOpen={destroyModal.isOpen}
        onClose={closeDestroyModal}
        onConfirm={() => deleteInstanceMutation.mutate({ instanceId: destroyModal.instanceId })}
        title="Destruir Instância"
        message={`Esta ação é irreversível. A instância "${destroyModal.instanceName}" será completamente removida, incluindo todos os dados no disco.`}
        targetName={destroyModal.instanceName}
        confirmText="Destruir Instância"
        isLoading={deleteInstanceMutation.isPending}
      />
    </div>
  )
}

export default InstancesPage
